import {
  type PagedResponse,
  paginate,
  type RequestOptions,
  type RestSchema,
} from "@mountaya/client";
import { z } from "zod";

import type { RestClientConfig } from "../../rest/rules/sdk";
import { isNotFoundError, mountayaFor } from "../../rest/rules/sdk";

/**
 * billingFor is the published billing namespace for a config, holding every operation the
 * organization billing surface reaches under the credential the config carries.
 *
 * Every read and write below is ORGANIZATION-scoped, because every billable thing is: a
 * person buys nothing, so there is no self-scoped customer for an invoice, a stored card
 * or a tax identity to belong to. That scoping is also what makes the namespace publishable:
 * the account an invoice belongs to is exactly the account a credential is issued for, so a
 * customer settling their own bill from a script reaches the same operations this console
 * does.
 *
 * It is declared here rather than imported from the organization domain's seam module, which
 * exports the identical binding, because the edge runs the other way: that module's queries
 * import {@link subscriptionDiscountSchema} from this one, and sharing the seam would close
 * the cycle.
 */
const billingFor = (config: RestClientConfig) => mountayaFor(config).admin.billing;

/**
 * subscriptionDiscountSchema mirrors the wire SubscriptionDiscount: the active discount
 * read off a scope's Stripe subscription. `label` is the human name (the coupon's name
 * when set, otherwise the customer-facing promotion code, otherwise the coupon's id). The
 * discount itself is one of `percent_off` (a percentage, mutually exclusive with the amount)
 * or `amount_off` (a fixed amount in the currency's minor units, paired with `currency`);
 * both amount fields are optional because a given coupon carries only one.
 *
 * `duration` is how long it lasts, in the promotion-code preview's vocabulary (`once`,
 * `repeating`, `forever`), declared a string on the contract because the vocabulary is the
 * payment provider's. `ends_at` is when a `repeating` discount stops applying, null for the
 * other two; it is `z.date()` because the transport revives the wire date-time before this
 * parses, as it does `current_period_end_at`.
 *
 * It lives here, in the shared billing module the org subscription slot imports, so the
 * discount has one definition rather than a duplicated parallel schema. A parity assertion in
 * the tests locks it to the generated wire type so the two never drift.
 */
const subscriptionDiscountSchema = z.object({
  amount_off: z.number().optional(),
  currency: z.string().optional(),
  duration: z.string(),
  ends_at: z.date().nullable(),
  label: z.string(),
  percent_off: z.number().optional(),
}) satisfies z.ZodType<SubscriptionDiscount>;

/**
 * SubscriptionDiscount is the active discount on a subscription, and it is the CONTRACT's
 * type: the schema above parses every field the contract declares, so the alias claims
 * nothing the parse drops. The Data API subscription carries it as an optional `discount`
 * (the promotion code given when the organization's billing was set up, applied to its first
 * subscription), and the billing page reads it to state the discount beside the plan.
 */
export type SubscriptionDiscount = RestSchema.SubscriptionDiscount;

/**
 * subscriptionDiscountSchema is re-exported so the org-subscription schema in its own
 * domain module composes this one definition for its optional `discount` field.
 */
export { subscriptionDiscountSchema };

/**
 * paymentMethodCardSchema mirrors the wire PaymentMethodCard: the display brand, the
 * last four digits, and the expiry month/year of a card-type payment method. Projected
 * from Stripe for display only (no PAN), present only when the parent method's type is
 * "card", so it is the optional `card` sub-object on paymentMethodSchema.
 */
const paymentMethodCardSchema = z.object({
  brand: z.string(),
  exp_month: z.number(),
  exp_year: z.number(),
  last4: z.string(),
}) satisfies z.ZodType<PaymentMethodCard>;

/**
 * PaymentMethodCard is a card payment method's display details, and it is the contract's
 * type.
 */
export type PaymentMethodCard = RestSchema.PaymentMethodCard;

/**
 * paymentMethodSepaDebitSchema mirrors the wire PaymentMethodSepaDebit: the display
 * details of a SEPA Direct Debit method — the last four IBAN characters and the
 * two-letter ISO country of the bank account, with the optional bank and branch codes.
 * Projected from Stripe for display only (no full IBAN), present only when the parent
 * method's type is "sepa_debit", so it is the optional `sepa_debit` sub-object on
 * paymentMethodSchema.
 */
const paymentMethodSepaDebitSchema = z.object({
  bank_code: z.string().optional(),
  branch_code: z.string().optional(),
  country: z.string(),
  last4: z.string(),
}) satisfies z.ZodType<PaymentMethodSepaDebit>;

/**
 * PaymentMethodSepaDebit is a SEPA Direct Debit method's display details, and it is the
 * contract's type.
 */
export type PaymentMethodSepaDebit = RestSchema.PaymentMethodSepaDebit;

/**
 * paymentMethodSchema mirrors the wire PaymentMethod: a saved payment method on a
 * payer's Stripe customer for the manage-cards list. `id` set-defaults or detaches it,
 * `type` discriminates the instrument ("card" or "sepa_debit" for self-serve),
 * `is_default` marks the customer's default method, and the type-specific display
 * sub-object carries the masked details: `card` (brand/last4/expiry) when the type is
 * "card", `sepa_debit` (last4/country) when the type is "sepa_debit". Both sub-objects
 * are optional and mutually exclusive per method — exactly the one matching `type` is
 * present — so a method of a type this surface does not yet detail parses and renders
 * gracefully from its type label rather than failing the list.
 */
const paymentMethodSchema = z.object({
  card: paymentMethodCardSchema.optional(),
  id: z.string(),
  is_default: z.boolean(),
  sepa_debit: paymentMethodSepaDebitSchema.optional(),
  type: z.string(),
}) satisfies z.ZodType<PaymentMethod>;

/**
 * PaymentMethod is a saved payment method, and it is the contract's type.
 *
 * `type` is a free string on both sides rather than a union discriminating the two
 * sub-objects, and that is the contract's shape rather than a looseness here: an
 * instrument this surface does not detail still has to list and still has to carry its
 * own type label, so the sub-objects are optional siblings and a reader takes the one its
 * `type` names.
 */
export type PaymentMethod = RestSchema.PaymentMethod;

/**
 * addressSchema mirrors the wire Address: the postal billing address on the Stripe
 * customer (line1, line2, city, postal_code, state, country). Every field is optional so
 * a partial address round-trips on both the read (BillingInfo) and write
 * (BillingInfoUpdateParams) sides; an all-empty address clears it server-side.
 */
const addressSchema = z.object({
  city: z.string().optional(),
  country: z.string().optional(),
  line1: z.string().optional(),
  line2: z.string().optional(),
  postal_code: z.string().optional(),
  state: z.string().optional(),
});

/**
 * BillingAddress is the api-layer view of a billing address, derived from addressSchema.
 * Named BillingAddress (not Address) so it never collides with the DOM Address concept
 * at a call site.
 */
export type BillingAddress = z.infer<typeof addressSchema>;

/**
 * taxIdSchema mirrors the wire TaxId read back from Stripe: the Stripe tax-id `type`
 * (for example "eu_vat") and the registration `value`. Both are present on the read
 * side; the write side (taxIdParamsSchema) makes the type optional with an "eu_vat"
 * default.
 */
const taxIdSchema = z.object({
  type: z.string(),
  value: z.string(),
}) satisfies z.ZodType<TaxId>;

/**
 * TaxId is a customer tax id, and it is the contract's type.
 */
export type TaxId = RestSchema.TaxId;

/**
 * billingInfoSchema mirrors the wire BillingInfo: the payer's Stripe customer display
 * name + email (read-only, synced from the identity/org profile), the billing address
 * (omitted when the customer has none), and the tax id (omitted when none is on file).
 * The page renders name + email read-only and edits only the address + tax id through
 * the update endpoint.
 */
const billingInfoSchema = z.object({
  address: addressSchema.optional(),
  email: z.string(),
  name: z.string(),
  tax_id: taxIdSchema.optional(),
}) satisfies z.ZodType<BillingInfo>;

/**
 * BillingInfo is a payer's billing information, and it is the contract's type: the schema
 * above parses every field the contract declares, so the alias claims nothing the parse
 * drops.
 */
export type BillingInfo = RestSchema.BillingInfo;

/**
 * taxIdParamsSchema mirrors the wire BillingInfoTaxIdParams: the tax-id portion of a
 * billing-info update. The customer supplies `value` (the VAT number; an empty string
 * clears any existing tax id); `type` is optional and the backend defaults it to
 * "eu_vat", the only self-serve tax-id type, when omitted.
 */
const taxIdParamsSchema = z.object({
  type: z.string().optional(),
  value: z.string(),
});

/**
 * billingInfoUpdateSchema mirrors the wire BillingInfoUpdateParams: the writable slice
 * of billing info — the Stripe customer name + email, the address, and the tax id. The
 * name + email are applied for an organization only (the org billing surface edits them);
 * the user billing-info endpoint ignores them and keeps name + email synced from the
 * account profile. Every field is replace-semantics: an omitted field leaves it unchanged,
 * an all-empty address clears the address, and an empty tax-id value clears the tax id. The
 * billing form parses its submit through this before sending so the request shape is
 * validated at the boundary.
 */
export const billingInfoUpdateSchema = z.object({
  address: addressSchema.optional(),
  email: z.string().optional(),
  name: z.string().optional(),
  tax_id: taxIdParamsSchema.optional(),
});

/**
 * BillingInfoUpdate is the api-layer billing-info update body, derived from
 * billingInfoUpdateSchema. It is the shape useUpdateBillingInfo's mutation variable
 * takes.
 */
export type BillingInfoUpdate = z.infer<typeof billingInfoUpdateSchema>;

/**
 * invoiceStatusSchema mirrors the contract's InvoiceStatus: a closed set, in lifecycle order
 *, an invoice is drafted, opened for payment, and then settles one of three ways.
 *
 * It is an enum rather than a string because the set is closed on the wire and the table
 * branches on it: `hosted_invoice_url` and `invoice_pdf` are absent while the status is
 * `draft` and present from `open` onwards, so a pay button is decided by this field before it
 * is decided by either link. `satisfies` holds it to the contract, and is HALF the guarantee
 * for the reason `roles.ts` spells out, a NARROWER union still satisfies a wider one, so a
 * status the contract gains and this schema lacks would pass here and reject a real invoice at
 * the parse. The type-level equality in `queries.test.ts` holds that other direction, which
 * is what this is exported for, the option list is unreachable from an alias of the status.
 */
export const invoiceStatusSchema = z.enum([
  "draft",
  "open",
  "paid",
  "void",
  "uncollectible",
]) satisfies z.ZodType<RestSchema.InvoiceStatus>;

/**
 * invoiceSchema mirrors the wire Invoice: a past or open invoice on the payer's Stripe
 * customer for the invoice-history table. Amounts are in the smallest currency unit
 * exactly as Stripe reports them (no money is computed client-side); `created_at` is a Date
 * (the client's transport revives the wire ISO string into a real Date); `number`,
 * `hosted_invoice_url`, and `invoice_pdf` are optional because a draft invoice has no
 * number or PDF yet.
 */
const invoiceSchema = z.object({
  amount_due: z.number(),
  created_at: z.date(),
  currency: z.string(),
  hosted_invoice_url: z.string().optional(),
  id: z.string(),
  invoice_pdf: z.string().optional(),
  number: z.string().optional(),
  status: invoiceStatusSchema,
  total: z.number(),
}) satisfies z.ZodType<Invoice>;

/**
 * Invoice is one invoice on the payer's Stripe customer, and it is the contract's type:
 * the schema above parses every field the contract declares, so the alias claims nothing
 * the parse drops.
 */
export type Invoice = RestSchema.Invoice;

/**
 * billingInfoResponseSchema is the REST envelope around the payer's billing info, as a write
 * answers it: the information after the change, in full.
 */
const billingInfoResponseSchema = z.object({ data: billingInfoSchema });

/**
 * billingInfoReadResponseSchema is the REST envelope around the billing-info READ, whose `data`
 * is null while the organization has no billing account yet: there is nothing to address an
 * invoice to until its first payment method opens one.
 */
const billingInfoReadResponseSchema = z.object({ data: billingInfoSchema.nullable() });

/**
 * billingQueryKey is the TanStack Query key root for one organization's billing reads. It
 * keys under the org id so two organizations a manager belongs to never collide, and the
 * leaf (passed by the per-resource key factories) distinguishes the payment-methods,
 * billing-info, and invoices entries within one.
 */
const billingQueryKey = (organizationId: string, leaf: string) =>
  ["billing", "organization", organizationId, leaf] as const;

/**
 * paymentMethodsQueryKey is the query key for an organization's saved payment methods. A
 * detach / set-default / add-card flow invalidates this key so the list re-pulls.
 */
export const paymentMethodsQueryKey = (organizationId: string) =>
  billingQueryKey(organizationId, "payment-methods");

/**
 * billingInfoQueryKey is the query key for an organization's billing info (address + tax
 * id + read-only name/email). An update invalidates this key so the form re-pulls.
 */
export const billingInfoQueryKey = (organizationId: string) =>
  billingQueryKey(organizationId, "info");

/**
 * invoicesQueryKey is the query key for an organization's invoice history. No console write
 * creates or edits an invoice, but a card attached, a default pinned and a Data API run pay the
 * invoices still open, so each of those writes invalidates this key.
 */
export const invoicesQueryKey = (organizationId: string) =>
  billingQueryKey(organizationId, "invoices");

/**
 * readBillingListing walks one of the organization's billing listings to its end, parsing each
 * row as the walk answers it, and reads a listing whose FIRST page is answered 404 as empty.
 *
 * The listings keep the 404 their contract declares for an organization with no billing account
 * yet (ADR 0120): no Stripe customer exists until a first card provisions one, and an
 * organization billed by agreement may never have one. That is no failed read but a state, and
 * the one thing it says about the listing is that it holds nothing, so the surfaces read it as
 * their empty state ("no invoices yet", and the way to add a first card) rather than as a load
 * failure whose Retry can only ever get the same 404. It is the same reading the billing-info
 * read gives the same state, where the API answers `data` null instead.
 *
 * Only before any page has been answered: a 404 on a later page is a listing that stopped being
 * there mid-walk, which is a failure, and it throws as one. Every other failure throws as the
 * typed error its status classifies to.
 *
 * @param operation The billing namespace's listing operation, as `paginate` takes it.
 * @param args The operation's own arguments.
 * @param parse The row's parse, run on each row as the walk answers it.
 */
const readBillingListing = async <A extends object | undefined, T, R>(
  operation: (args: A, options?: RequestOptions) => Promise<PagedResponse<T>>,
  args: NonNullable<A>,
  parse: (row: T) => R,
): Promise<R[]> => {
  let pagesAnswered = 0;
  const counted = async (pageArgs: A, options?: RequestOptions): Promise<PagedResponse<T>> => {
    const page = await operation(pageArgs, options);
    pagesAnswered += 1;

    return page;
  };

  const rows: R[] = [];
  try {
    for await (const row of paginate(counted, args)) {
      rows.push(parse(row));
    }
  } catch (error) {
    if (pagesAnswered === 0 && isNotFoundError(error)) {
      return [];
    }

    throw error;
  }

  return rows;
};

/**
 * fetchPaymentMethods is a hookless query-fn reading an organization's saved payment methods,
 * whole ([] when the customer has none, and when the organization has no billing account yet
 * for a payment method to be saved on). The client projects the config's auth strategy onto
 * the request and throws a typed error on a non-ok response — the same contract as every other
 * read here.
 *
 * The methods are served a page at a time, the most recently saved first, and the manage-cards
 * list shows every one of them: the SDK's `paginate` walks the pages as {@link fetchInvoices}
 * does, following `next_cursor` until it reads null, and each method is Zod-parsed as the walk
 * answers it ({@link readBillingListing}).
 */
export const fetchPaymentMethods = (
  config: RestClientConfig,
  organizationId: string,
): Promise<PaymentMethod[]> =>
  readBillingListing(
    billingFor(config).getOrganizationBillingPaymentMethods,
    { organization_id: organizationId },
    (row) => paymentMethodSchema.parse(row),
  );

/**
 * detachPaymentMethod removes one saved payment method (by Stripe id) from an
 * organization's customer. The success envelope carries nothing the caller reads (the page
 * invalidates the payment-method list to re-pull), so this resolves to void. Removing the last
 * method is allowed. A non-ok response (a 403 for a non-manager, a 409 for the default method
 * while another one could be made the default first, a state the request met rather than
 * anything it carried) throws a typed error.
 */
export const detachPaymentMethod = async (
  config: RestClientConfig,
  organizationId: string,
  paymentMethodId: string,
): Promise<void> => {
  await billingFor(config).deleteOrganizationBillingPaymentMethod({
    organization_id: organizationId,
    payment_method_id: paymentMethodId,
  });
};

/**
 * setDefaultPaymentMethod pins one saved payment method (by Stripe id) as an
 * organization's customer default, the method invoices and subscriptions charge. The success envelope
 * carries nothing the caller reads (the page invalidates the list so the default badge
 * moves), so this resolves to void. A non-ok response (a 403 for a non-manager) throws a
 * typed error.
 */
export const setDefaultPaymentMethod = async (
  config: RestClientConfig,
  organizationId: string,
  paymentMethodId: string,
): Promise<void> => {
  await billingFor(config).postOrganizationBillingPaymentMethodSetDefault({
    organization_id: organizationId,
    payment_method_id: paymentMethodId,
  });
};

/**
 * fetchBillingInfo is a hookless query-fn reading an organization's billing info (the read-only
 * name + email plus the editable address + tax id), or null while the organization has no
 * billing account yet: the read answers `data` null then rather than a 404, so a page tells "not
 * set up yet" from "not there". Same auth/parse/throw contract as the other reads.
 */
export const fetchBillingInfo = async (
  config: RestClientConfig,
  organizationId: string,
): Promise<BillingInfo | null> => {
  const envelope = await billingFor(config).getOrganizationBillingInfo({
    organization_id: organizationId,
  });

  return billingInfoReadResponseSchema.parse(envelope).data;
};

/**
 * updateBillingInfo PATCHes an organization's billing-info update (the address + tax id) and
 * resolves to the re-read billing info. Each member present replaces its field: an omitted
 * field is left unchanged, an all-empty address clears it, an empty tax-id value clears the
 * tax id.
 * The envelope is Zod-parsed at the boundary. A non-ok response (a 403 for a
 * non-manager, a 400 for an invalid VAT, a 409 for an organization with no billing account
 * yet) throws a typed error.
 */
export const updateBillingInfo = async (
  config: RestClientConfig,
  organizationId: string,
  params: BillingInfoUpdate,
): Promise<BillingInfo> => {
  // Member by member: the API refuses a body member it does not declare, and a form state can
  // carry more than the four it does.
  const envelope = await billingFor(config).patchOrganizationBillingInfo({
    body: {
      address: params.address,
      email: params.email,
      name: params.name,
      tax_id: params.tax_id,
    },
    organization_id: organizationId,
  });

  return billingInfoResponseSchema.parse(envelope).data;
};

/**
 * fetchInvoices is a hookless query-fn reading an organization's invoice history, whole ([]
 * when the customer has none, and when the organization has no billing account yet to be
 * invoiced on). The client's transport revives each invoice's wire `created_at` ISO string into
 * a real Date. Same auth/throw contract as the other reads.
 *
 * The history is served a page at a time. The SDK's `paginate` is the walk: it sends `limit` and
 * the `cursor` the previous page named under `query`, where the operation reads them, and
 * follows `next_cursor` until it reads null. Each invoice is Zod-parsed as the walk answers it
 * ({@link readBillingListing}).
 */
export const fetchInvoices = (
  config: RestClientConfig,
  organizationId: string,
): Promise<Invoice[]> =>
  readBillingListing(
    billingFor(config).getOrganizationBillingInvoices,
    { organization_id: organizationId },
    (row) => invoiceSchema.parse(row),
  );

/**
 * paymentMethodsQueryOptions bundles an organization's payment-method query key + fetch fn so
 * the useQuery hook and any render-free caller share one cache entry.
 */
export const paymentMethodsQueryOptions = (config: RestClientConfig, organizationId: string) => ({
  queryFn: () => fetchPaymentMethods(config, organizationId),
  queryKey: paymentMethodsQueryKey(organizationId),
});

/**
 * billingInfoQueryOptions bundles an organization's billing-info query key + fetch fn.
 */
export const billingInfoQueryOptions = (config: RestClientConfig, organizationId: string) => ({
  queryFn: () => fetchBillingInfo(config, organizationId),
  queryKey: billingInfoQueryKey(organizationId),
});

/**
 * invoicesQueryOptions bundles an organization's invoices query key + fetch fn.
 */
export const invoicesQueryOptions = (config: RestClientConfig, organizationId: string) => ({
  queryFn: () => fetchInvoices(config, organizationId),
  queryKey: invoicesQueryKey(organizationId),
});

/**
 * PromotionCodePreview is the read-only preview the validate endpoint returns for a
 * Stripe promotion code resolved against the organization's own customer: whether the
 * code is valid and eligible, and (only when valid) the resolved coupon's label, its
 * discount (a percentage or a fixed amount in the currency's minor units), and how long
 * it lasts. When valid is false every other field is absent and the code's existence is
 * not disclosed.
 */
export type PromotionCodePreview = RestSchema.PromotionCodePreview;

/**
 * promotionCodePreviewSchema mirrors the wire PromotionCodePreview the validate endpoint
 * returns: a validity flag plus, only when valid, the resolved coupon's label, its
 * discount (mutually exclusive percent_off / amount_off, amount in the currency's minor
 * units with its currency), and how long it lasts. Every discount field is optional
 * because the endpoint omits them all when the code is invalid or ineligible, the field
 * shape itself never reveals whether a private code exists. The billing UI parses the
 * envelope with this at the boundary and renders the discount line or an inline "not
 * valid" from the result. A parity assertion in the tests locks it to the generated wire
 * type so the two never drift.
 */
const promotionCodePreviewSchema = z.object({
  amount_off: z.number().optional(),
  currency: z.string().optional(),
  duration: z.string().optional(),
  label: z.string().optional(),
  percent_off: z.number().optional(),
  valid: z.boolean(),
}) satisfies z.ZodType<PromotionCodePreview>;

/**
 * promotionCodePreviewResponseSchema is the REST envelope around the promotion-code
 * preview. The data is always present (the endpoint returns a preview with valid false
 * rather than a 404 for an unknown or ineligible code, so the code's existence is never
 * disclosed), so the inner schema is not nullable.
 */
const promotionCodePreviewResponseSchema = z.object({
  data: promotionCodePreviewSchema,
});

/**
 * promotionCodePreviewQueryKey is the query key for a promotion-code preview. The code is
 * part of the key so each entered code caches its own preview and a re-validation of the
 * same code is served from cache; the org segment keeps two organizations' previews of the
 * same code distinct (a code can resolve differently per customer).
 */
export const promotionCodePreviewQueryKey = (organizationId: string, code: string) =>
  ["billing", "organization", organizationId, "promotion-code", code] as const;

/**
 * PROMOTION_CODE_SHAPE is the one shape a promotion code may have: 1 to 64 letters, digits,
 * `_` and `-`, the contract's pattern for the code the preview takes in its path. A value of
 * any other shape is refused 400 at `["request", "params", "code"]` and never looked up.
 */
const PROMOTION_CODE_SHAPE = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * isWellFormedPromotionCode reports whether a code, as it will be sent, has the shape the
 * contract allows (PROMOTION_CODE_SHAPE): one of any other shape can only be refused, so a
 * caller says it is not valid without asking.
 */
export const isWellFormedPromotionCode = (code: string): boolean => PROMOTION_CODE_SHAPE.test(code);

/**
 * fetchPromotionCodePreview is a hookless query-fn validating a customer-facing Stripe
 * promotion code against the organization's own Stripe customer and resolving to the
 * discount preview. The customer is resolved server-side, so only the org and the code
 * travel, never a customer id. The code rides the path: the contract allows letters, digits,
 * `_` and `-` only (isWellFormedPromotionCode), none of which the path reserves, and a value
 * of any other shape is refused 400 at the code without being looked up. Same auth/parse/throw
 * contract as the other reads: the client projects the config's auth strategy onto the
 * request and Zod-parses the envelope at the boundary, throwing a typed error on a non-ok
 * response. A valid-but-ineligible or unknown code is NOT an error: it comes back as a
 * parsed preview with valid false, so the UI shows a uniform "not valid" without
 * disclosing whether the code exists.
 */
export const fetchPromotionCodePreview = async (
  config: RestClientConfig,
  organizationId: string,
  code: string,
): Promise<PromotionCodePreview> => {
  const envelope = await billingFor(config).getOrganizationBillingPromotionCode({
    code,
    organization_id: organizationId,
  });

  return promotionCodePreviewResponseSchema.parse(envelope).data;
};

/**
 * promotionCodePreviewQueryOptions bundles the preview query key + fetch fn, gated by an
 * `enabled` flag the caller drives. The flag is false until the caller explicitly
 * validates a non-empty trimmed code, so typing into the field never fetches and the
 * endpoint is hit only on an Apply: the validate is on-demand, not reactive. A code of a
 * shape the contract refuses (isWellFormedPromotionCode) is never sent either. The preview
 * is not retried and is held only briefly (it is single-use confirmation, not long-lived
 * server state the app reads back), so the caller pairs this with a short or default
 * stale window.
 */
export const promotionCodePreviewQueryOptions = (
  config: RestClientConfig,
  organizationId: string,
  code: string,
) => {
  const trimmed = code.trim();

  return {
    enabled: isWellFormedPromotionCode(trimmed),
    queryFn: () => fetchPromotionCodePreview(config, organizationId, trimmed),
    queryKey: promotionCodePreviewQueryKey(organizationId, trimmed),
    retry: false,
  };
};
